// ============================================
// Master Formulator — Seed Formulary Products
// 10 representative products across verticals
// ============================================

import type { FormularyProduct } from '../../types/masterFormulator';

export const SEED_FORMULARY_PRODUCTS: Partial<FormularyProduct>[] = [
  // ─────────────────────────────────────────────
  // 1. DHEA 25mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-001',
    productName: 'DHEA 25mg',
    productDisplayName: 'DHEA 25mg Capsules',
    productType: 'supplement',
    vertical: 'hormone_health',
    category: 'hormone_precursor',
    subcategory: 'prohormone',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-DHEA',
        ingredientName: 'DHEA (Dehydroepiandrosterone)',
        form: 'Micronized DHEA',
        amountPerServing: 25,
        unit: 'mg',
        percentDV: null,
        isActive: true,
      },
    ],
    allergens: [],
    form: 'capsule',
    servingSize: '1 capsule',
    servingsPerContainer: 60,
    maxDailyDose: '1 capsule (25mg)',
    dosingNotes: 'Take in the morning with food. Do not exceed 25mg/day without provider supervision. Monitor DHEA-S levels after 6-8 weeks.',
    warnings: [
      'May affect hormone-sensitive conditions',
      'Not recommended for individuals under 30 without clinical indication',
      'May cause acne, oily skin, or hair changes at higher doses',
      'Discontinue 2 weeks before surgery',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-DHEA-001',
        condition: 'hormone_sensitive_cancer',
        severity: 'absolute',
        description: 'DHEA converts to androgens and estrogens; contraindicated in breast, prostate, ovarian, and endometrial cancers',
        actionRequired: 'block',
        source: 'NatMed Professional Database',
      },
      {
        contraindicationId: 'CI-DHEA-002',
        condition: 'pregnancy',
        severity: 'absolute',
        description: 'DHEA is a hormone precursor with potential teratogenic effects',
        actionRequired: 'block',
        source: 'FDA Dietary Supplement Guidance',
      },
      {
        contraindicationId: 'CI-DHEA-003',
        condition: 'polycystic_ovary_syndrome',
        severity: 'relative',
        description: 'May worsen androgen excess symptoms in PCOS',
        actionRequired: 'warn_provider',
        source: 'Endocrine Society Guidelines',
      },
    ],
    pregnancyCategory: 'X',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['DHEA-S levels at 6-8 weeks', 'Testosterone levels at 12 weeks', 'Liver function panel annually'],
    evidenceGrade: 'B',
    healthClaims: [
      {
        claimId: 'HC-DHEA-001',
        claimType: 'structure_function',
        claimText: 'Supports healthy hormone levels',
        evidenceBasis: 'Multiple RCTs demonstrate DHEA supplementation restores age-related DHEA-S decline in adults over 40',
        evidenceGrade: 'B',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: true,
        disclaimerText: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'NSF',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 2. Vitamin D3 5000 IU
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-002',
    productName: 'Vitamin D3 5000 IU',
    productDisplayName: 'Vitamin D3 5000 IU Softgels',
    productType: 'supplement',
    vertical: 'foundation',
    category: 'vitamin',
    subcategory: 'fat_soluble_vitamin',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-VITD3',
        ingredientName: 'Vitamin D3 (Cholecalciferol)',
        form: 'Cholecalciferol',
        amountPerServing: 5000,
        unit: 'IU',
        percentDV: 625,
        isActive: true,
        bioavailabilityNotes: 'Oil-based softgel for enhanced fat-soluble absorption',
      },
    ],
    allergens: ['soy'],
    form: 'softgel',
    servingSize: '1 softgel',
    servingsPerContainer: 90,
    maxDailyDose: '1 softgel (5000 IU)',
    dosingNotes: 'Take with a fat-containing meal for optimal absorption. Pair with Vitamin K2 to direct calcium to bones. Recheck 25(OH)D at 8-12 weeks.',
    warnings: [
      'Do not exceed 10,000 IU/day without medical supervision',
      'Monitor calcium levels with prolonged high-dose use',
      'May interact with thiazide diuretics (hypercalcemia risk)',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-VITD3-001',
        condition: 'hypercalcemia',
        severity: 'absolute',
        description: 'Vitamin D increases calcium absorption; contraindicated with elevated serum calcium',
        actionRequired: 'block',
        source: 'Endocrine Society Practice Guidelines 2024',
      },
      {
        contraindicationId: 'CI-VITD3-002',
        condition: 'granulomatous_disease',
        severity: 'absolute',
        description: 'Sarcoidosis and other granulomatous diseases cause unregulated 1,25(OH)2D production',
        actionRequired: 'block',
        source: 'NatMed Professional Database',
      },
      {
        contraindicationId: 'CI-VITD3-003',
        condition: 'primary_hyperparathyroidism',
        severity: 'relative',
        description: 'May exacerbate hypercalcemia in primary hyperparathyroidism',
        actionRequired: 'warn_provider',
        source: 'AACE Guidelines',
      },
    ],
    pregnancyCategory: 'A',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['25(OH)D at 8-12 weeks', 'Serum calcium annually', 'PTH if calcium elevated'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-VITD3-001',
        claimType: 'health',
        claimText: 'Adequate vitamin D intake is important for bone health and may reduce the risk of osteoporosis',
        evidenceBasis: 'Multiple meta-analyses demonstrate vitamin D supplementation reduces fracture risk and supports bone mineral density',
        evidenceGrade: 'A',
        fdaNotified: true,
        fdaNotificationDate: '2009-01-01',
        disclaimerRequired: false,
        disclaimerText: null,
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'USP',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 3. Omega-3 Fish Oil
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-003',
    productName: 'Omega-3 Fish Oil',
    productDisplayName: 'Omega-3 Fish Oil 2000mg (EPA/DHA)',
    productType: 'supplement',
    vertical: 'cardiovascular',
    category: 'fatty_acid',
    subcategory: 'essential_fatty_acid',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-EPA',
        ingredientName: 'EPA (Eicosapentaenoic Acid)',
        form: 'Triglyceride-form EPA',
        amountPerServing: 1200,
        unit: 'mg',
        percentDV: null,
        isActive: true,
        bioavailabilityNotes: 'Triglyceride form, 70% more bioavailable than ethyl ester',
      },
      {
        ingredientId: 'ING-DHA',
        ingredientName: 'DHA (Docosahexaenoic Acid)',
        form: 'Triglyceride-form DHA',
        amountPerServing: 800,
        unit: 'mg',
        percentDV: null,
        isActive: true,
        bioavailabilityNotes: 'Triglyceride form for enhanced absorption',
      },
    ],
    allergens: ['fish'],
    form: 'softgel',
    servingSize: '2 softgels',
    servingsPerContainer: 60,
    maxDailyDose: '2 softgels (2000mg EPA+DHA)',
    dosingNotes: 'Take with meals to reduce fishy aftertaste and improve absorption. May split dose AM/PM. Refrigerate for freshness.',
    warnings: [
      'May increase bleeding risk in patients on anticoagulants',
      'Discontinue 1-2 weeks before surgical procedures',
      'Monitor for fishy aftertaste, GI upset',
      'High doses (>3g/day) may raise LDL cholesterol',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-OMEGA3-001',
        condition: 'fish_allergy',
        severity: 'absolute',
        description: 'Derived from fish; contraindicated in patients with fish allergy',
        actionRequired: 'block',
        source: 'FDA allergen labeling requirements',
      },
      {
        contraindicationId: 'CI-OMEGA3-002',
        condition: 'active_bleeding_disorder',
        severity: 'relative',
        description: 'EPA/DHA have antiplatelet effects that may worsen bleeding',
        actionRequired: 'warn_provider',
        source: 'AHA Scientific Statement on Omega-3 Fatty Acids',
      },
    ],
    pregnancyCategory: 'B',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['Omega-3 index at 8-12 weeks', 'Lipid panel at 12 weeks', 'INR if on anticoagulants'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-OMEGA3-001',
        claimType: 'qualified_health',
        claimText: 'Supportive but not conclusive research shows that consumption of EPA and DHA omega-3 fatty acids may reduce the risk of coronary heart disease',
        evidenceBasis: 'FDA qualified health claim based on REDUCE-IT, VITAL, and STRENGTH trials',
        evidenceGrade: 'A',
        fdaNotified: true,
        fdaNotificationDate: '2019-06-19',
        disclaimerRequired: true,
        disclaimerText: 'The FDA has evaluated the data and determined that the evidence is supportive but not conclusive.',
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'IFOS',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 4. Ashwagandha 600mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-004',
    productName: 'Ashwagandha 600mg',
    productDisplayName: 'Ashwagandha KSM-66 600mg',
    productType: 'supplement',
    vertical: 'hormone_health',
    category: 'botanical',
    subcategory: 'adaptogen',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-ASHWAGANDHA',
        ingredientName: 'Ashwagandha (Withania somnifera)',
        form: 'KSM-66 Root Extract',
        amountPerServing: 600,
        unit: 'mg',
        percentDV: null,
        isActive: true,
        standardizedExtract: 'KSM-66',
        bioavailabilityNotes: 'Full-spectrum root extract, standardized to 5% withanolides',
      },
    ],
    allergens: ['nightshade'],
    form: 'capsule',
    servingSize: '1 capsule',
    servingsPerContainer: 60,
    maxDailyDose: '2 capsules (1200mg)',
    dosingNotes: 'Take 1 capsule in the morning with food. May take second capsule in the evening for sleep support. Allow 4-8 weeks for full adaptogenic effect.',
    warnings: [
      'May potentiate thyroid hormone medication',
      'May cause drowsiness at higher doses',
      'Nightshade family — caution with nightshade sensitivity',
      'Discontinue 2 weeks before surgery (sedative interaction potential)',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-ASHWA-001',
        condition: 'hyperthyroidism',
        severity: 'absolute',
        description: 'Ashwagandha may increase thyroid hormone levels, contraindicated in uncontrolled hyperthyroidism',
        actionRequired: 'block',
        source: 'Sharma AK et al. J Altern Complement Med. 2018;24(3):243-248.',
      },
      {
        contraindicationId: 'CI-ASHWA-002',
        condition: 'autoimmune_thyroid_disease',
        severity: 'relative',
        description: 'May stimulate thyroid function unpredictably in Hashimoto or Graves disease',
        actionRequired: 'warn_provider',
        source: 'NatMed Professional Database',
      },
      {
        contraindicationId: 'CI-ASHWA-003',
        condition: 'pregnancy',
        severity: 'absolute',
        description: 'Insufficient safety data in pregnancy; possible abortifacient properties in high doses',
        actionRequired: 'block',
        source: 'WHO Monograph on Withania somnifera',
      },
    ],
    pregnancyCategory: 'X',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['TSH and free T4 at 8 weeks if on thyroid medication', 'Cortisol levels optional at 12 weeks'],
    evidenceGrade: 'B',
    healthClaims: [
      {
        claimId: 'HC-ASHWA-001',
        claimType: 'structure_function',
        claimText: 'Helps the body adapt to stress and supports healthy cortisol levels',
        evidenceBasis: 'Multiple RCTs demonstrate KSM-66 reduces serum cortisol by 23-30% over 60 days',
        evidenceGrade: 'B',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: true,
        disclaimerText: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'ndi_notified',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: 'NDI-1042',
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'USP',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 5. Magnesium Glycinate 400mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-005',
    productName: 'Magnesium Glycinate 400mg',
    productDisplayName: 'Magnesium Glycinate 400mg Capsules',
    productType: 'supplement',
    vertical: 'foundation',
    category: 'mineral',
    subcategory: 'macro_mineral',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-MAGNESIUM-GLYCINATE',
        ingredientName: 'Magnesium (as Magnesium Bisglycinate Chelate)',
        form: 'Magnesium Bisglycinate Chelate (TRAACS)',
        amountPerServing: 400,
        unit: 'mg',
        percentDV: 95,
        isActive: true,
        bioavailabilityNotes: 'Chelated glycinate form — high bioavailability, minimal GI side effects compared to oxide or citrate',
      },
    ],
    allergens: [],
    form: 'capsule',
    servingSize: '2 capsules',
    servingsPerContainer: 60,
    maxDailyDose: '2 capsules (400mg elemental magnesium)',
    dosingNotes: 'Take in the evening 30-60 minutes before bed. May split into AM/PM dosing. Take separately from calcium supplements by 2 hours. Separate from antibiotics and bisphosphonates by 2-4 hours.',
    warnings: [
      'High doses may cause loose stools (less likely with glycinate form)',
      'Separate from fluoroquinolone antibiotics by 2+ hours',
      'Separate from bisphosphonates by 2+ hours',
      'May potentiate muscle relaxants and blood pressure medications',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-MAG-001',
        condition: 'severe_renal_impairment',
        severity: 'absolute',
        description: 'Kidneys cannot excrete excess magnesium; risk of hypermagnesemia (GFR <30)',
        actionRequired: 'block',
        source: 'NIH Office of Dietary Supplements Fact Sheet',
      },
      {
        contraindicationId: 'CI-MAG-002',
        condition: 'myasthenia_gravis',
        severity: 'relative',
        description: 'Magnesium may worsen neuromuscular junction dysfunction',
        actionRequired: 'warn_provider',
        source: 'NatMed Professional Database',
      },
    ],
    pregnancyCategory: 'A',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['RBC magnesium at 8-12 weeks (serum Mg is unreliable)', 'Renal function if high-dose or prolonged use'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-MAG-001',
        claimType: 'nutrient_content',
        claimText: 'Excellent source of magnesium',
        evidenceBasis: 'Contains 95% Daily Value of magnesium per serving',
        evidenceGrade: 'A',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: false,
        disclaimerText: null,
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'NSF',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 6. Berberine 500mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-006',
    productName: 'Berberine 500mg',
    productDisplayName: 'Berberine HCl 500mg Capsules',
    productType: 'supplement',
    vertical: 'metabolic',
    category: 'botanical',
    subcategory: 'metabolic_support',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-BERBERINE',
        ingredientName: 'Berberine (as Berberine Hydrochloride)',
        form: 'Berberine HCl',
        amountPerServing: 500,
        unit: 'mg',
        percentDV: null,
        isActive: true,
        bioavailabilityNotes: 'HCl salt form for improved solubility; consider dihydroberberine for enhanced bioavailability',
      },
    ],
    allergens: [],
    form: 'capsule',
    servingSize: '1 capsule',
    servingsPerContainer: 60,
    maxDailyDose: '3 capsules (1500mg)',
    dosingNotes: 'Take 500mg 2-3 times daily with meals. Start with 500mg once daily for 1 week to assess GI tolerance. Spread doses evenly to maintain blood levels (short half-life).',
    warnings: [
      'May cause GI side effects (diarrhea, constipation, flatulence) especially at initiation',
      'Potent CYP3A4 and CYP2D6 inhibitor — check for drug interactions',
      'May potentiate hypoglycemic effects of metformin and insulin',
      'Do not combine with cyclosporine, statins metabolized by CYP3A4, or macrolide antibiotics without provider approval',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-BERB-001',
        condition: 'pregnancy',
        severity: 'absolute',
        description: 'Berberine may cause uterine contractions and has shown teratogenic effects in animal studies',
        actionRequired: 'block',
        source: 'NatMed Professional Database',
      },
      {
        contraindicationId: 'CI-BERB-002',
        condition: 'lactation',
        severity: 'absolute',
        description: 'Berberine transfers to breast milk; may cause kernicterus in neonates',
        actionRequired: 'block',
        source: 'WHO Traditional Medicine Guidelines',
      },
      {
        contraindicationId: 'CI-BERB-003',
        condition: 'severe_hypoglycemia',
        severity: 'relative',
        description: 'May cause dangerous hypoglycemia when combined with insulin or sulfonylureas',
        actionRequired: 'warn_provider',
        source: 'Yin J et al. Metabolism. 2008;57(5):712-717.',
      },
    ],
    pregnancyCategory: 'X',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['Fasting glucose and HbA1c at 8-12 weeks', 'Lipid panel at 12 weeks', 'Liver function at 12 weeks', 'Monitor for GI side effects at 2 weeks'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-BERB-001',
        claimType: 'structure_function',
        claimText: 'Supports healthy blood sugar levels already within normal range',
        evidenceBasis: 'Meta-analysis of 27 RCTs shows berberine reduces fasting glucose by 15-25 mg/dL comparable to metformin 500mg',
        evidenceGrade: 'A',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: true,
        disclaimerText: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'ndi_notified',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: 'NDI-0873',
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'ConsumerLab',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 7. CoQ10 200mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-007',
    productName: 'CoQ10 200mg',
    productDisplayName: 'CoQ10 (Ubiquinol) 200mg Softgels',
    productType: 'supplement',
    vertical: 'cardiovascular',
    category: 'enzyme',
    subcategory: 'coenzyme',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-COQ10',
        ingredientName: 'Coenzyme Q10 (as Ubiquinol)',
        form: 'Ubiquinol (reduced form)',
        amountPerServing: 200,
        unit: 'mg',
        percentDV: null,
        isActive: true,
        bioavailabilityNotes: 'Ubiquinol (reduced form) is 3-6x more bioavailable than ubiquinone, especially in adults over 40',
      },
    ],
    allergens: [],
    form: 'softgel',
    servingSize: '1 softgel',
    servingsPerContainer: 60,
    maxDailyDose: '2 softgels (400mg)',
    dosingNotes: 'Take with a fat-containing meal. Ubiquinol form preferred over ubiquinone for adults 40+. Essential co-supplement for anyone on statin therapy.',
    warnings: [
      'May reduce effectiveness of warfarin (structurally similar to vitamin K)',
      'May potentiate antihypertensive medications',
      'Very rare: mild insomnia if taken late in the day',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-COQ10-001',
        condition: 'warfarin_therapy',
        severity: 'relative',
        description: 'CoQ10 is structurally similar to vitamin K and may reduce warfarin effectiveness; requires INR monitoring',
        actionRequired: 'warn_provider',
        source: 'Heck AM et al. Pharmacotherapy. 2000;20(3):266-276.',
      },
    ],
    pregnancyCategory: 'B',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['CoQ10 serum levels optional at 12 weeks', 'INR if on warfarin', 'Blood pressure if on antihypertensives'],
    evidenceGrade: 'B',
    healthClaims: [
      {
        claimId: 'HC-COQ10-001',
        claimType: 'structure_function',
        claimText: 'Supports cellular energy production and cardiovascular function',
        evidenceBasis: 'CoQ10 is essential for mitochondrial electron transport chain; Q-SYMBIO trial showed 43% reduction in cardiovascular events with CoQ10 supplementation in heart failure patients',
        evidenceGrade: 'B',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: true,
        disclaimerText: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'USP',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 8. Zinc 30mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-008',
    productName: 'Zinc 30mg',
    productDisplayName: 'Zinc Picolinate 30mg Capsules',
    productType: 'supplement',
    vertical: 'immune',
    category: 'mineral',
    subcategory: 'trace_mineral',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-ZINC',
        ingredientName: 'Zinc (as Zinc Picolinate)',
        form: 'Zinc Picolinate',
        amountPerServing: 30,
        unit: 'mg',
        percentDV: 273,
        isActive: true,
        bioavailabilityNotes: 'Picolinate form shows superior absorption vs sulfate and gluconate forms',
      },
    ],
    allergens: [],
    form: 'capsule',
    servingSize: '1 capsule',
    servingsPerContainer: 60,
    maxDailyDose: '1 capsule (30mg)',
    dosingNotes: 'Take with food to minimize nausea. Separate from iron supplements by 2 hours. If taking long-term (>3 months), add 1-2mg copper to prevent copper depletion.',
    warnings: [
      'Long-term use above 40mg/day may cause copper deficiency',
      'Separate from fluoroquinolone and tetracycline antibiotics by 2+ hours',
      'May cause nausea on empty stomach',
      'High doses may impair immune function paradoxically',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-ZINC-001',
        condition: 'copper_deficiency',
        severity: 'relative',
        description: 'Zinc competes with copper for absorption; may worsen copper deficiency',
        actionRequired: 'warn_provider',
        source: 'NIH Office of Dietary Supplements Fact Sheet',
      },
    ],
    pregnancyCategory: 'A',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['Serum zinc at 8-12 weeks', 'Serum copper and ceruloplasmin if >3 months use', 'CBC if prolonged high-dose use'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-ZINC-001',
        claimType: 'nutrient_content',
        claimText: 'Excellent source of zinc for immune support',
        evidenceBasis: 'Contains 273% Daily Value; zinc is an essential cofactor for 300+ enzymes',
        evidenceGrade: 'A',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: false,
        disclaimerText: null,
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'NSF',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 9. B-Complex
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-009',
    productName: 'B-Complex',
    productDisplayName: 'Methylated B-Complex',
    productType: 'supplement',
    vertical: 'foundation',
    category: 'multi_ingredient',
    subcategory: 'b_complex',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-B1',
        ingredientName: 'Vitamin B1 (Thiamine)',
        form: 'Thiamine HCl',
        amountPerServing: 100,
        unit: 'mg',
        percentDV: 8333,
        isActive: true,
      },
      {
        ingredientId: 'ING-B2',
        ingredientName: 'Vitamin B2 (Riboflavin)',
        form: 'Riboflavin-5-Phosphate',
        amountPerServing: 25,
        unit: 'mg',
        percentDV: 1923,
        isActive: true,
        bioavailabilityNotes: 'Active coenzyme form (R5P) — no hepatic conversion required',
      },
      {
        ingredientId: 'ING-B3',
        ingredientName: 'Vitamin B3 (Niacin)',
        form: 'Niacinamide',
        amountPerServing: 50,
        unit: 'mg',
        percentDV: 313,
        isActive: true,
        bioavailabilityNotes: 'Niacinamide form avoids flushing associated with nicotinic acid',
      },
      {
        ingredientId: 'ING-B5',
        ingredientName: 'Vitamin B5 (Pantothenic Acid)',
        form: 'Calcium D-Pantothenate',
        amountPerServing: 100,
        unit: 'mg',
        percentDV: 2000,
        isActive: true,
      },
      {
        ingredientId: 'ING-B6',
        ingredientName: 'Vitamin B6 (Pyridoxine)',
        form: 'Pyridoxal-5-Phosphate (P5P)',
        amountPerServing: 25,
        unit: 'mg',
        percentDV: 1471,
        isActive: true,
        bioavailabilityNotes: 'P5P is the active coenzyme form — critical for MTHFR variants',
      },
      {
        ingredientId: 'ING-B7',
        ingredientName: 'Vitamin B7 (Biotin)',
        form: 'D-Biotin',
        amountPerServing: 300,
        unit: 'mcg',
        percentDV: 1000,
        isActive: true,
      },
      {
        ingredientId: 'ING-B9',
        ingredientName: 'Vitamin B9 (Folate)',
        form: 'L-5-Methyltetrahydrofolate (5-MTHF)',
        amountPerServing: 800,
        unit: 'mcg DFE',
        percentDV: 200,
        isActive: true,
        bioavailabilityNotes: 'Methylated folate — bypasses MTHFR enzyme; critical for ~40% of population with MTHFR variants',
      },
      {
        ingredientId: 'ING-B12',
        ingredientName: 'Vitamin B12 (Cobalamin)',
        form: 'Methylcobalamin',
        amountPerServing: 1000,
        unit: 'mcg',
        percentDV: 41667,
        isActive: true,
        bioavailabilityNotes: 'Methylcobalamin is the active, methylated form — no conversion required',
      },
    ],
    allergens: [],
    form: 'capsule',
    servingSize: '1 capsule',
    servingsPerContainer: 60,
    maxDailyDose: '1 capsule',
    dosingNotes: 'Take in the morning with food. Methylated forms (P5P, 5-MTHF, methylcobalamin) are essential for individuals with MTHFR variants. May cause bright yellow urine (riboflavin) — this is normal and harmless.',
    warnings: [
      'High-dose B6 (>100mg/day) over prolonged periods may cause peripheral neuropathy',
      'Biotin >300mcg may interfere with thyroid and troponin lab assays — discontinue 48 hours before blood work',
      'B12 levels may appear falsely elevated if already supplementing',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-BCOMP-001',
        condition: 'cobalt_allergy',
        severity: 'absolute',
        description: 'Vitamin B12 contains cobalt; contraindicated in cobalt-allergic patients',
        actionRequired: 'block',
        source: 'NatMed Professional Database',
      },
    ],
    pregnancyCategory: 'A',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['Homocysteine at 8-12 weeks', 'B12 and folate at 12 weeks', 'MMA (methylmalonic acid) if B12 borderline'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-BCOMP-001',
        claimType: 'structure_function',
        claimText: 'Supports energy metabolism, nervous system function, and methylation',
        evidenceBasis: 'B vitamins are essential cofactors in energy metabolism (Krebs cycle), neurotransmitter synthesis, and one-carbon methylation pathways',
        evidenceGrade: 'A',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: true,
        disclaimerText: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'USP',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },

  // ─────────────────────────────────────────────
  // 10. Iron Bisglycinate 25mg
  // ─────────────────────────────────────────────
  {
    productId: 'MF-PROD-010',
    productName: 'Iron Bisglycinate 25mg',
    productDisplayName: 'Iron Bisglycinate 25mg Capsules',
    productType: 'supplement',
    vertical: 'foundation',
    category: 'mineral',
    subcategory: 'trace_mineral',
    tier: 1,
    ingredients: [
      {
        ingredientId: 'ING-IRON-BISGLYCINATE',
        ingredientName: 'Iron (as Ferrous Bisglycinate Chelate)',
        form: 'Ferrochel Ferrous Bisglycinate Chelate',
        amountPerServing: 25,
        unit: 'mg',
        percentDV: 139,
        isActive: true,
        standardizedExtract: 'Ferrochel',
        bioavailabilityNotes: 'Bisglycinate chelate — 4x better absorbed than ferrous sulfate with 80% fewer GI side effects',
      },
    ],
    allergens: [],
    form: 'capsule',
    servingSize: '1 capsule',
    servingsPerContainer: 60,
    maxDailyDose: '1 capsule (25mg)',
    dosingNotes: 'Take on empty stomach with vitamin C for optimal absorption, or with food if GI intolerance occurs. Separate from calcium, zinc, coffee, and tea by 2 hours. Separate from thyroid medication (levothyroxine) by 4 hours. Separate from antibiotics by 2-4 hours.',
    warnings: [
      'Iron overload risk — do NOT take without confirmed deficiency (ferritin <30)',
      'Separate from levothyroxine by 4 hours (chelation reduces thyroid med absorption)',
      'Separate from tetracycline and fluoroquinolone antibiotics by 2-4 hours',
      'Keep out of reach of children (iron overdose is a leading cause of pediatric poisoning)',
      'May cause constipation, dark stools',
    ],
    contraindications: [
      {
        contraindicationId: 'CI-IRON-001',
        condition: 'hemochromatosis',
        severity: 'absolute',
        description: 'Iron supplementation is contraindicated in hereditary hemochromatosis and iron overload states',
        actionRequired: 'block',
        source: 'AASLD Practice Guidelines for Hemochromatosis',
      },
      {
        contraindicationId: 'CI-IRON-002',
        condition: 'hemolytic_anemia',
        severity: 'absolute',
        description: 'Iron supplementation may worsen iron overload in hemolytic anemias',
        actionRequired: 'block',
        source: 'ASH Clinical Practice Guidelines',
      },
      {
        contraindicationId: 'CI-IRON-003',
        condition: 'repeated_blood_transfusions',
        severity: 'relative',
        description: 'Patients receiving regular transfusions are at risk for iatrogenic iron overload',
        actionRequired: 'warn_provider',
        source: 'NIH NHLBI Guidelines',
      },
    ],
    pregnancyCategory: 'A',
    requiresRx: false,
    requiresProvider: false,
    monitoringRequirements: ['Ferritin at 8-12 weeks', 'CBC with reticulocyte count at 8 weeks', 'TIBC and transferrin saturation at 12 weeks', 'Discontinue or reduce once ferritin >50'],
    evidenceGrade: 'A',
    healthClaims: [
      {
        claimId: 'HC-IRON-001',
        claimType: 'nutrient_content',
        claimText: 'Excellent source of iron in a gentle, well-absorbed form',
        evidenceBasis: 'Ferrous bisglycinate chelate is demonstrated to have superior bioavailability and tolerability compared to ferrous sulfate in multiple RCTs',
        evidenceGrade: 'A',
        fdaNotified: false,
        fdaNotificationDate: null,
        disclaimerRequired: false,
        disclaimerText: null,
      },
    ],
    regulatoryStatus: {
      fdaClassification: 'gras',
      fdaDisclaimer: 'This statement has not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.',
      ndaNumber: null,
      ndiNotificationNumber: null,
      gmpCompliant: true,
      thirdPartyTested: true,
      thirdPartyTestingOrg: 'USP',
      labelClaimCompliant: true,
      stateRestrictions: [],
    },
    version: 1,
    previousVersionId: null,
    versionNotes: null,
    transitionProtocol: null,
    lastSafetyScore: null,
    lastEvidenceScore: null,
    lastRegulatoryScore: null,
    lastFormularyReadinessScore: null,
    lastScoredAt: null,
    lastScoreId: null,
    status: 'draft',
    createdAt: '2026-02-18T00:00:00Z',
    createdBy: 'system-seed',
    updatedAt: '2026-02-18T00:00:00Z',
    updatedBy: 'system-seed',
  },
];
